import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import { useCreateOnboarding, useOAuthApps } from "@/api/hooks";
import type { OnboardingRunSummary } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
	onCreated: (run: OnboardingRunSummary) => void;
}

/** Step 1 of the wizard — collects basic info + vendor selections,
 * then calls `POST /onboarding` to create the pending run. */
export function IntakeForm({ onCreated }: Props) {
	const create = useCreateOnboarding();
	const oauthApps = useOAuthApps();

	const [slug, setSlug] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [adminEmail, setAdminEmail] = useState("");
	const [region, setRegion] = useState("iad");
	const [selectedVendors, setSelectedVendors] = useState<Set<string>>(new Set());

	function toggleVendor(vendor: string, id: string) {
		setSelectedVendors((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			// vendor param reserved in case we later key by vendor name.
			void vendor;
			return next;
		});
	}

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		try {
			const run = await create.mutateAsync({
				slug,
				display_name: displayName,
				contact_email: contactEmail,
				admin_email: adminEmail,
				region,
				vendor_selections: Array.from(selectedVendors),
			});
			onCreated(run);
		} catch {
			/* surfaced via create.error */
		}
	}

	const errorMessage =
		create.error instanceof ApiError
			? create.error.detail
			: create.error
				? "Unable to create run."
				: null;

	const configuredApps = oauthApps.data?.apps ?? [];

	return (
		<Card>
			<CardHeader>
				<CardTitle>New client onboarding</CardTitle>
				<CardDescription>
					Fill in client details + pick which Veridon-level OAuth apps to enable. This step just
					creates a pending run; the next page actually provisions.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<form className="space-y-4" onSubmit={onSubmit} noValidate>
					<div className="grid gap-4 md:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="slug">Slug</Label>
							<Input
								id="slug"
								required
								pattern="^[a-z0-9][a-z0-9-]*$"
								placeholder="acme"
								value={slug}
								onChange={(e) => setSlug(e.target.value)}
							/>
							<p className="text-xs text-muted-foreground">
								Lowercase + digits + hyphens. Becomes{" "}
								<span className="font-mono">{slug || "acme"}.veridonconsulting.com</span>.
							</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="display-name">Display name</Label>
							<Input
								id="display-name"
								required
								placeholder="Acme Law"
								value={displayName}
								onChange={(e) => setDisplayName(e.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="admin-email">Admin email</Label>
							<Input
								id="admin-email"
								type="email"
								required
								placeholder="admin@acme.com"
								value={adminEmail}
								onChange={(e) => setAdminEmail(e.target.value)}
							/>
							<p className="text-xs text-muted-foreground">
								First user bootstrapped on the tenant portal. Gets the welcome email.
							</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="contact-email">Primary contact email</Label>
							<Input
								id="contact-email"
								type="email"
								required
								placeholder="billing@acme.com"
								value={contactEmail}
								onChange={(e) => setContactEmail(e.target.value)}
							/>
						</div>
						<div className="space-y-2 md:col-span-2">
							<Label htmlFor="region">Fly region</Label>
							<Input
								id="region"
								required
								value={region}
								onChange={(e) => setRegion(e.target.value.trim())}
							/>
							<p className="text-xs text-muted-foreground">
								Fly code (see fly.io/docs/reference/regions). Default{" "}
								<span className="font-mono">iad</span>.
							</p>
						</div>
					</div>

					<div className="space-y-2">
						<Label>Veridon OAuth apps to push</Label>
						{oauthApps.isLoading ? (
							<p className="text-xs text-muted-foreground">Loading…</p>
						) : configuredApps.length === 0 ? (
							<p className="text-xs text-muted-foreground">
								No OAuth apps configured yet. Paste vendor creds in{" "}
								<a href="/settings" className="underline">
									Settings → OAuth apps
								</a>{" "}
								first, or proceed without — the tenant portal still deploys; it just won't have
								OAuth providers until a later pass.
							</p>
						) : (
							<ul className="grid gap-1 md:grid-cols-2">
								{configuredApps.map((app) => (
									<li key={app.vendor}>
										<label className="flex cursor-pointer items-center gap-2 text-sm">
											<input
												type="checkbox"
												className="h-4 w-4"
												checked={selectedVendors.has(app.vendor)}
												onChange={() => toggleVendor(app.vendor, app.vendor)}
											/>
											<span>{app.vendor.replace(/_/g, " ")}</span>
										</label>
									</li>
								))}
							</ul>
						)}
						<p className="text-xs text-muted-foreground">
							Each checked vendor's client_id / client_secret gets pushed to the new tenant's Fly
							secrets after deploy.
						</p>
					</div>

					{errorMessage ? (
						<p className="text-sm text-destructive" role="alert">
							{errorMessage}
						</p>
					) : null}

					<div className="flex justify-end gap-2">
						<Button
							type="submit"
							disabled={create.isPending || !slug || !displayName || !adminEmail || !contactEmail}
						>
							{create.isPending ? "Creating…" : "Create run"}
						</Button>
					</div>
				</form>
			</CardContent>
		</Card>
	);
}
